export function installPrerequisiteModal(Room) {
    const proto = Room.prototype;
    const submit = proto.submitChatMessage;
    const openPicker = proto.addPrerequisiteDocument;
    const closePicker = proto.closeDriveBrowser;

    proto.focusPrerequisiteDialog = function () {
        if (typeof document !== 'undefined') document.querySelector('.prerequisite-modal__close')?.focus();
    };

    proto.showPrerequisiteDialog = async function (decision, request) {
        this.pendingPrerequisiteRequest = request;
        this.inspectionPrerequisiteNotice = {
            missing_prerequisites: decision.missing_prerequisites || [],
            eligible_checks: decision.eligible_checks || [],
            message: decision.message || ''
        };
        this.showInspectionFlowModal = false;
        await this.service.render();
        this.focusPrerequisiteDialog();
    };
    proto.prerequisiteDecision = function (checks) {
        const missing = this.localInspectionPrerequisites(checks);
        const blocked = new Set(missing.map(item => item.check));
        return { missing_prerequisites: missing, eligible_checks: checks.filter(check => !blocked.has(check)) };
    };
    proto.startInspectionFlow = async function () {
        if (this.sending || this.isQuestionQuotaExceeded) return;
        const checks = this.getSelectedInspectionChecks();
        if (!checks.length) return;
        const decision = this.prerequisiteDecision(checks);
        if (decision.missing_prerequisites.length) {
            await this.showPrerequisiteDialog(decision, { source: 'selection', checks, message: this.buildInspectionFlowMessage(checks) });
            return;
        }
        await this.runPreparedInspectionChecks(checks);
    };
    proto.runPreparedInspectionChecks = async function (checks, message = '') {
        if (!checks.length || this.sending || this.isQuestionQuotaExceeded) return;
        const flow = { mode: 'sequential_inspection', checks, requested_checks: checks, labels: checks.map(check => this.inspectionCheckLabel(check)) };
        // Only explicitly selected ready checks are submitted. Missing checks are
        // not sent as requested_checks, so the server cannot silently add/skip them.
        const accepted = await this.submitChatMessage(message || this.buildInspectionFlowMessage(checks), flow);
        if (accepted) {
            this.inspectionPrerequisiteNotice = null;
            this.pendingPrerequisiteRequest = null;
            this.showInspectionFlowModal = false;
            if (message && this.inputMessage?.trim() === message) this.inputMessage = '';
        }
        await this.service.render();
    };
    proto.startPreparedPrerequisiteChecks = async function () {
        const request = this.pendingPrerequisiteRequest;
        if (!request || this.sending || this.isQuestionQuotaExceeded) return;
        const decision = this.prerequisiteDecision(request.checks);
        const previouslyReady = new Set(this.inspectionPrerequisiteNotice?.eligible_checks || []);
        const checks = decision.eligible_checks.filter(check => previouslyReady.has(check));
        if (!checks.length) { await this.showPrerequisiteDialog(decision, request); return; }
        await this.runPreparedInspectionChecks(checks, request.source === 'chat' ? request.message : '');
    };
    proto.submitChatMessage = async function (message, flow = null) {
        this._prerequisiteSubmission = { source: this.showInspectionFlowModal ? 'selection' : 'chat', message, flow, checks: flow?.requested_checks || flow?.checks || [] };
        try {
            const accepted = await submit.call(this, message, flow);
            if (accepted) this.pendingPrerequisiteRequest = null;
            return accepted;
        } finally { this._prerequisiteSubmission = null; }
    };
    // Server-side prerequisite failures, including typed inspection requests,
    // use the same modal. A legacy skipped result must not reopen a running flow.
    proto.setInspectionPrerequisiteNotice = function (data, skipped = false) {
        if (skipped) return;
        const pending = this._prerequisiteSubmission || this.pendingPrerequisiteRequest || { source: 'selection' };
        const checks = data?.requested_checks?.length ? data.requested_checks : pending.checks || this.getSelectedInspectionChecks();
        this.pendingPrerequisiteRequest = { ...pending, checks };
        this.inspectionPrerequisiteNotice = { missing_prerequisites: data?.missing_prerequisites || [], eligible_checks: data?.eligible_checks || [], message: data?.message || '' };
        this.showInspectionFlowModal = false;
        const notice = this.inspectionPrerequisiteNotice;
        void this.service.render().then(() => {
            if (this.inspectionPrerequisiteNotice === notice) this.focusPrerequisiteDialog();
        });
    };
    Object.defineProperty(proto, 'inspectionStartLabel', { configurable: true, get() { return this.sending ? '시작 요청 중...' : '검사 시작'; } });

    proto.addPrerequisiteDocument = async function (role) {
        if (this.sending) return;
        this._returnFromPrerequisitePicker = !!this.pendingPrerequisiteRequest;
        this.inspectionPrerequisiteNotice = null;
        await openPicker.call(this, role);
    };
    proto.closeDriveBrowser = async function () {
        await closePicker.call(this);
        if (!this._returnFromPrerequisitePicker) return;
        this._returnFromPrerequisitePicker = false;
        const request = this.pendingPrerequisiteRequest;
        if (request) await this.showPrerequisiteDialog(this.prerequisiteDecision(request.checks), request);
    };
    proto.dismissInspectionPrerequisiteNotice = async function () {
        this.inspectionPrerequisiteNotice = null;
        this.pendingPrerequisiteRequest = null;
        this._returnFromPrerequisitePicker = false;
        await this.service.render();
    };
    proto.returnToInspectionSelection = async function () {
        const checks = this.pendingPrerequisiteRequest?.checks || [];
        await this.dismissInspectionPrerequisiteNotice();
        for (const item of this.inspectionFlowOptions) item.enabled = checks.includes(item.id);
        this.showInspectionFlowModal = true;
        await this.service.render();
    };
    proto.onPrerequisiteBackdrop = async function (event) {
        if (event.target === event.currentTarget && !this.sending) await this.dismissInspectionPrerequisiteNotice();
    };
    proto.onPrerequisiteModalKeydown = async function (event) {
        if (event.key === 'Escape' && !this.sending) {
            event.preventDefault(); event.stopPropagation();
            await this.dismissInspectionPrerequisiteNotice();
        } else if (event.key === 'Tab') {
            const buttons = Array.from(event.currentTarget.querySelectorAll('button:not([disabled])')).filter(button => button.getClientRects().length);
            const first = buttons[0], last = buttons[buttons.length - 1];
            if (first && last && (event.shiftKey ? event.target === first : event.target === last)) {
                event.preventDefault(); (event.shiftKey ? last : first).focus();
            }
        }
    };
}
